import { z } from "zod";

/**
 * Ano Sync — the structured "Sync Event" data carried inside a normal
 * message's `payload` JSONB under the `ano_sync` marker key.
 *
 * Why a payload marker instead of a new message `kind`:
 *   Adding a `message_kind` enum value is a cross-surface change (Postgres
 *   enum + iOS Swift mirror) gated by ci:kind-derivation-test-parity. A
 *   Sync Event is instead a normal `kind="text"` message whose `payload`
 *   carries this object. `validatePayload("text", …)` preserves arbitrary
 *   payload opaquely (message-payload-validation.ts), iOS decodes unknown
 *   payload fields silently (Message.swift `[String: AnyCodable]`), and old
 *   desktop clients fall through to rendering the message `content` as text.
 *   The desktop SyncEventCard + Control Tower key off `payload.ano_sync`.
 *
 * Keys are camelCase to match the documented external POST /api/sync-events
 * contract — this is an opaque nested payload object, NOT a DB column, so the
 * snake_case column convention does not apply here.
 */

/** Marker key under `messages.payload` that flags a Sync Event message. */
export const ANO_SYNC_PAYLOAD_KEY = "ano_sync" as const;

export const SYNC_EVENT_SOURCES = [
  "claude_code",
  "codex",
  "cursor",
  "github",
  "linear",
  "terminal",
  "manual",
  "system",
] as const;
export type SyncEventSource = (typeof SYNC_EVENT_SOURCES)[number];

export const SYNC_EVENT_ACTOR_TYPES = [
  "human",
  "agent",
  "tool",
  "system",
] as const;
export type SyncEventActorType = (typeof SYNC_EVENT_ACTOR_TYPES)[number];

export const SYNC_EVENT_TYPES = [
  "work_started",
  "progress",
  "decision",
  "blocker",
  "question",
  "tests",
  "review_requested",
  "completed",
  "failed",
] as const;
export type SyncEventType = (typeof SYNC_EVENT_TYPES)[number];

export const SYNC_EVENT_STATUSES = [
  "info",
  "success",
  "warning",
  "error",
] as const;
export type SyncEventStatus = (typeof SYNC_EVENT_STATUSES)[number];

export const SYNC_TEST_STATUSES = [
  "passed",
  "failed",
  "skipped",
  "unknown",
] as const;
export type SyncTestStatus = (typeof SYNC_TEST_STATUSES)[number];

export const syncTestSchema = z.object({
  name: z.string().min(1),
  status: z.enum(SYNC_TEST_STATUSES),
  details: z.string().optional(),
});
export type SyncTest = z.infer<typeof syncTestSchema>;

// Same http(s)-only refine as linkOnlyPayloadSchema (message-payload.ts):
// link buttons feed `openExternalUrl`, so reject `javascript:` / `data:`
// schemes at the schema boundary.
const httpUrl = z
  .string()
  .url()
  .refine((u) => /^https?:\/\//i.test(u), {
    message: "url must use http or https scheme",
  });

export const syncLinkSchema = z.object({
  label: z.string().min(1),
  url: httpUrl,
});
export type SyncLink = z.infer<typeof syncLinkSchema>;

/**
 * The Sync Event itself. Only `source`, `actorName`, `eventType`, and `title`
 * are required; every structured section is optional and omitted-when-empty by
 * the renderers. `actorType` and `status` carry sensible defaults so minimal
 * producers (a bare `ano_send_sync_event` curl) still validate.
 */
export const syncEventPayloadSchema = z.object({
  source: z.enum(SYNC_EVENT_SOURCES),
  actorType: z.enum(SYNC_EVENT_ACTOR_TYPES).default("agent"),
  actorName: z.string().min(1),
  eventType: z.enum(SYNC_EVENT_TYPES),
  status: z.enum(SYNC_EVENT_STATUSES).default("info"),
  title: z.string().min(1),
  summary: z.string().optional(),
  changedFiles: z.array(z.string()).optional(),
  commandsRun: z.array(z.string()).optional(),
  tests: z.array(syncTestSchema).optional(),
  decisions: z.array(z.string()).optional(),
  risks: z.array(z.string()).optional(),
  blockers: z.array(z.string()).optional(),
  nextSteps: z.array(z.string()).optional(),
  links: z.array(syncLinkSchema).optional(),
  externalSessionId: z.string().optional(),
  externalRunId: z.string().optional(),
  rawPayload: z.record(z.string(), z.unknown()).optional(),
  /** Epoch ms — stamped server-side at ingestion. */
  createdAt: z.number().optional(),
});
export type SyncEventPayload = z.infer<typeof syncEventPayloadSchema>;

/**
 * Pull the Sync Event out of a message's opaque `payload`, if present.
 * Returns `null` for normal (non-sync) messages. Used by the desktop render
 * branch, the Control Tower selector, and the server channel-context read so
 * all three agree on what counts as a Sync Event.
 */
export function extractSyncEvent(
  payload: Record<string, unknown> | null | undefined,
): SyncEventPayload | null {
  if (!payload || typeof payload !== "object") return null;
  const raw = (payload as Record<string, unknown>)[ANO_SYNC_PAYLOAD_KEY];
  if (!raw || typeof raw !== "object") return null;
  const parsed = syncEventPayloadSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Wrap a validated Sync Event into the `messages.payload` JSONB shape. */
export function toSyncMessagePayload(
  event: SyncEventPayload,
): Record<string, unknown> {
  return { [ANO_SYNC_PAYLOAD_KEY]: event };
}

/**
 * Human-readable one-line fallback stamped into the message `content` so old
 * desktop clients and iOS (which don't render the rich card) still show
 * something useful, and so `preview_text` / push notifications read sanely.
 */
export function syncEventContentFallback(event: SyncEventPayload): string {
  return event.summary ? `${event.title} — ${event.summary}` : event.title;
}
